'use client';

import React from 'react';
import Link from 'next/link';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Layers, 
  List, 
  CalendarDays, 
  ExternalLink, 
  Users, 
  Ticket, 
  Plus, 
  Bell 
} from 'lucide-react';
import { BookingViewMode } from './types';

interface BookingsHeaderProps {
  viewMode: BookingViewMode;
  onChangeViewMode: (mode: BookingViewMode) => void;
  selectedDate: string; // YYYY-MM-DD
  onSelectDate: (date: string) => void;
  onOpenNewBookingModal: () => void;
}

export const BookingsHeader: React.FC<BookingsHeaderProps> = ({
  viewMode,
  onChangeViewMode,
  selectedDate,
  onSelectDate,
  onOpenNewBookingModal,
}) => {
  // Generar la semana del día seleccionado (Lunes a Domingo)
  const curr = new Date(`${selectedDate}T12:00:00Z`);
  const dayOfWeek = curr.getUTCDay(); // 0: Dom, 1: Lun, ..., 6: Sáb
  const distanceToMonday = (dayOfWeek + 6) % 7;
  
  const monday = new Date(curr);
  monday.setUTCDate(curr.getUTCDate() - distanceToMonday);

  const weekDays = [];
  const dayInitials = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    const dayNum = d.getUTCDate().toString();
    weekDays.push({
      initial: dayInitials[i],
      dayNum,
      dateStr,
      isCurrent: dateStr === selectedDate,
    });
  }

  const handleShiftDay = (delta: number) => {
    const nextD = new Date(`${selectedDate}T12:00:00Z`);
    nextD.setUTCDate(nextD.getUTCDate() + delta);
    onSelectDate(nextD.toISOString().split('T')[0]);
  };

  const handleGoToday = () => {
    onSelectDate('2026-10-13');
  };

  const pageTitle =
    viewMode === 'agenda' ? 'Agenda' : viewMode === 'lista' ? 'Lista' : 'Mes';

  return (
    <div className="bg-white border-b border-slate-200/90 px-4 sm:px-6 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-4">
      {/* Lado Izquierdo: Título y Navegación de Días con Pastillas (AppGenda Fase 3) */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-black text-[#0f172a] tracking-tight">
          {pageTitle}
        </h1>

        {/* Cinta de días de la semana */}
        <div className="flex items-center gap-1 bg-slate-50 border border-slate-200/80 p-1 rounded-xl">
          <button
            onClick={() => handleShiftDay(-1)}
            aria-label="Día anterior"
            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-800 rounded-lg hover:bg-white transition cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1">
            {weekDays.map((wd) => {
              const isSelected = wd.isCurrent;
              return (
                <button
                  key={wd.dateStr}
                  onClick={() => onSelectDate(wd.dateStr)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    isSelected
                      ? 'bg-[#10b981] text-white shadow-xs'
                      : 'text-slate-600 hover:bg-slate-200/70'
                  }`}
                >
                  <span className="text-[10px] opacity-80">{wd.initial}</span>
                  <span>{wd.dayNum}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handleShiftDay(1)}
            aria-label="Día siguiente"
            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-800 rounded-lg hover:bg-white transition cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Botón Hoy y Selector de Mes */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleGoToday}
            className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition cursor-pointer"
          >
            Hoy
          </button>

          <button
            onClick={() => onChangeViewMode('mes')}
            className="text-xs font-bold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center gap-1 transition cursor-pointer"
          >
            <CalendarIcon className="w-3.5 h-3.5 text-slate-500" />
            <span>oct</span>
          </button>
        </div>
      </div>

      {/* Lado Derecho: Toggle de Vistas y Botones de Acción */}
      <div className="flex items-center gap-3 self-end md:self-auto">
        {/* Toggle [Agenda | Lista | Mes] */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200/80">
          <button
            onClick={() => onChangeViewMode('agenda')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              viewMode === 'agenda'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-[#0f172a]'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Agenda</span>
          </button>

          <button
            onClick={() => onChangeViewMode('lista')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              viewMode === 'lista'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-[#0f172a]'
            }`}
          >
            <List className="w-3.5 h-3.5" />
            <span>Lista</span>
          </button>

          <button
            onClick={() => onChangeViewMode('mes')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
              viewMode === 'mes'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-[#0f172a]'
            }`}
          >
            <CalendarDays className="w-3.5 h-3.5" />
            <span>Mes</span>
          </button>
        </div>

        {/* Acciones Rápidas */}
        <div className="flex items-center gap-2">
          {/* Enlace Público */}
          <Link
            href="/"
            target="_blank"
            title="Abrir portal público de reservas"
            className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>

          {/* Botón '+' Verde de Nuevo Turno */}
          <button
            onClick={onOpenNewBookingModal}
            title="Nuevo turno manual (POS)"
            className="w-9 h-9 rounded-full bg-[#10b981] hover:bg-[#059669] text-white flex items-center justify-center shadow-md transition cursor-pointer transform active:scale-95"
          >
            <Plus className="w-5 h-5" />
          </button>

          {/* Campana de Notificaciones con badge */}
          <div className="relative">
            <button
              title="Notificaciones"
              className="w-8 h-8 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"
            >
              <Bell className="w-4 h-4" />
            </button>
            <span className="absolute -top-1 -right-1 bg-rose-500 text-white font-extrabold text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
              3
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
